import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, RefreshCw } from "lucide-react";
import { Modal } from "../components/Modal";
import { PairingCodeHero } from "../components/PairingCodeHero";
import { devicesApi } from "../api/queries/devices";
import { useUiStore } from "../stores/ui";
import { toast } from "../components/Toast";
import { fleetKey, type FleetCache } from "../hooks/useFleet";

const HUB_HINT = (import.meta.env.VITE_HUB_URL as string | undefined) ?? "https://hub.paperclip.cloud";

type Os = "macos" | "linux" | "windows";

function snippet(os: Os, code: string): string {
  const formatted = code.replace(/\D/g, "").replace(/^(\d{3})(\d{3})$/, "$1-$2");
  if (os === "windows") {
    return `npx -y "@paperclipai/cloud-client" pair ${formatted} --hub "${HUB_HINT}"`;
  }
  return `npx -y @paperclipai/cloud-client pair ${formatted} --hub ${HUB_HINT}`;
}

export function PairDevice() {
  const open = useUiStore((s) => s.pairModalOpen);
  const setOpen = useUiStore((s) => s.setPairModalOpen);
  const companyId = useUiStore((s) => s.activeCompanyId);

  const [code, setCode] = useState<{ pairing_id: string; code: string; expires_at: number } | null>(
    null,
  );
  const [tab, setTab] = useState<Os>("macos");
  const [copied, setCopied] = useState(false);

  const qc = useQueryClient();

  const init = useMutation({
    mutationFn: () => {
      if (!companyId) throw new Error("no active company");
      return devicesApi.pairInit(companyId);
    },
    onSuccess: (data) => setCode(data),
    onError: (e: Error) =>
      toast(e.message ?? "Failed to start pairing — click “New code” to retry", "error"),
  });

  // Allow the "New code" button to clear the error state so the effect can fire again.
  const requestNewCode = () => {
    init.reset();
    setCode(null);
  };

  // Bootstrap a code exactly once when the modal opens. We must guard on
  // `init.isError` too — otherwise a 400 reply (which clears `isPending`)
  // immediately re-triggers the effect and hammers the endpoint until the
  // modal is closed. Pressing "New code" resets and tries again.
  useEffect(() => {
    if (
      open &&
      !code &&
      !init.isPending &&
      !init.isError &&
      companyId
    ) {
      init.mutate();
    }
    if (!open) {
      setCode(null);
      setCopied(false);
      init.reset();
    }
  }, [open, code, init.isPending, init.isError, companyId, init]);

  // Auto-close when the new device shows up in the fleet snapshot.
  useEffect(() => {
    if (!open || !code || !companyId) return;
    const seenIds = new Set(
      qc.getQueryData<FleetCache>(fleetKey(companyId))?.devices.map((d) => d.id) ?? [],
    );
    const unsubscribe = qc.getQueryCache().subscribe((evt) => {
      if (evt.type !== "updated") return;
      const cache = qc.getQueryData<FleetCache>(fleetKey(companyId));
      if (!cache) return;
      const fresh = cache.devices.find((d) => !seenIds.has(d.id));
      if (fresh) {
        toast(`${fresh.name} connected`, "success");
        setOpen(false);
      }
    });
    return unsubscribe;
  }, [open, code, companyId, qc, setOpen]);

  const cmd = useMemo(() => (code ? snippet(tab, code.code) : ""), [tab, code]);

  const onCopy = async () => {
    if (!cmd) return;
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop */
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Add a computer"
      size="md"
      footer={
        <>
          <button
            className="btn btn--ghost"
            onClick={requestNewCode}
            disabled={init.isPending}
            type="button"
          >
            <RefreshCw size={14} />
            New code
          </button>
          <button className="btn btn--secondary" onClick={() => setOpen(false)} type="button">
            Done
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-secondary">
          Run the install command on the computer you want to add. The pairing code is single-use
          and expires in 10 minutes.
        </p>
        {code ? (
          <PairingCodeHero code={code.code} expiresAt={code.expires_at} />
        ) : (
          <div className="card grid h-[140px] place-items-center text-secondary">
            <span className="spinner" aria-hidden="true" />
          </div>
        )}

        <div>
          <div role="tablist" aria-label="Operating system" className="flex gap-1 border-b border-border-subtle">
            {(["macos", "linux", "windows"] as Os[]).map((o) => (
              <button
                key={o}
                role="tab"
                aria-selected={tab === o}
                className={`px-3 py-2 text-[12px] font-medium ${
                  tab === o
                    ? "text-primary border-b-2 border-primary"
                    : "text-secondary hover:text-primary"
                }`}
                onClick={() => setTab(o)}
                type="button"
              >
                {o === "macos" ? "macOS" : o === "linux" ? "Linux" : "Windows"}
              </button>
            ))}
          </div>

          <div className="card mt-2 flex items-start gap-2 p-2">
            <pre className="flex-1 m-0 overflow-auto bg-sunken rounded-sm border border-border-subtle p-3 font-mono text-[12px] leading-[18px] text-primary">
              {cmd || "…"}
            </pre>
            <button
              type="button"
              onClick={onCopy}
              className="iconbtn"
              aria-label="Copy install command"
              title="Copy"
              disabled={!cmd}
            >
              {copied ? <Check size={16} className="text-status-running" /> : <Copy size={16} />}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-tertiary">
            Don’t have the client installed? It will be fetched on first run via{" "}
            <code className="font-mono">npx</code>.
          </p>
        </div>
      </div>
    </Modal>
  );
}
